/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * No-Dues Approver Review (web).
 *
 * Web port of the mobile `NoDuesApproverReviewScreen`: a mid-chain approver
 * (Deputy / CO / HOD …) reviews the employee-submitted No-Dues form and acts
 * on it (Approve / Reject / Send Back / Clarification) before HR dispatches it
 * to the clearing departments.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  CollapsibleSection,
  InfoRow,
  StatusBadge,
  ApprovalTimeline,
  ActionButtonGroup,
  ApproverRoleBadge,
  WarningBanner,
  OutstandingLoansSection,
  ClarificationThread,
  useConfirm,
} from "./shared/components";
import {
  getResignationDetails,
  getNoDuesFormDetails,
  submitApprovalAction,
  raiseClarification,
  getCurrentEmpNo,
  formatSeparationMode,
  ApprovalActionType,
  ApprovalStep,
  formatDate,
} from "./services/eSeparation.service";

/* Pretty labels for the employee-submitted No-Dues form fields that map
   directly (simple string value, no special Yes/No or conditional
   rendering) — training/bond/surety/declaration are rendered separately
   below since they need that extra logic. */
const FORM_FIELDS: { key: string; label: string; type?: "bool" }[] = [
  { key: "separation_mode", label: "Mode of Separation" },
  { key: "wing", label: "Wing" },
  { key: "notice_period_served", label: "Notice Period Served" },
  { key: "bond_amount", label: "Bond Amount" },
  { key: "leave_current_month", label: "Leave (Current Month)" },
  { key: "mobile", label: "Mobile" },
];

// Mirrors NoDuesForm.tsx's own NO_DUES_ADVANCE_TYPES exactly — the fixed
// 4-type surety table the employee fills in.
const ADVANCE_TYPE_LABELS: { code: string; label: string }[] = [
  { code: "HBA", label: "HBA Advance" },
  { code: "VEHICLE", label: "Vehicle Advance" },
  { code: "MULTIPURPOSE", label: "Multipurpose Advance" },
  { code: "DMRC_CREDIT_SOCIETY", label: "DMRC Credit Society" },
];

function formValue(
  form: Record<string, any> | null,
  key: string,
  type?: "bool"
): string {
  const v = form?.[key];
  if (v === null || v === undefined || v === "") return "—";
  if (type === "bool") return v ? "Yes" : "No";
  return String(v);
}

// Read the logged-in user's role (ADMIN / SUBADMIN can always act).
function currentUserRole(): string {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user?.role || "").toUpperCase();
  } catch {
    return "";
  }
}

const NoDuesApproverReview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { confirm, confirmDialog } = useConfirm();

  const state = (location.state as any) || {};
  const requestId: string = state.request_id || params.id || "";

  const loggedInEmpId = String(getCurrentEmpNo()).trim().padStart(8, "0");
  const userRole = currentUserRole();
  const isAdmin = userRole === "ADMIN" || userRole === "SUBADMIN";

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [request, setRequest] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any> | null>(null);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const [reqRes, ndRes] = await Promise.all([
        getResignationDetails(requestId),
        getNoDuesFormDetails(requestId),
      ]);
      if (reqRes.data) setRequest(reqRes.data);
      setForm(ndRes.data?.details ?? null);
    } catch {
      toast.error("Failed to load No-Dues request.");
    }
    setLoading(false);
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Current-approver ownership (drives the action panel) ── */
  const steps: any[] = Array.isArray(request?.steps) ? request.steps : [];
  const currentStep = steps.find(
    (s: any) => String(s?.status ?? "").toUpperCase() === "CURRENT"
  );
  const currentApproverEmpId = String(currentStep?.approver_emp_id ?? "")
    .trim()
    .padStart(8, "0");
  const isCurrentApprover =
    !!currentStep && currentApproverEmpId === loggedInEmpId;
  const requestStatus = String(request?.status ?? "").toUpperCase();
  const isTerminalStatus = ["APPROVED", "REJECTED", "WITHDRAWN"].includes(
    requestStatus
  );
  const canAct = !isTerminalStatus && (isAdmin || isCurrentApprover);
  const approverRole = currentStep?.role || "Approver";

  // A clarification chat stays private between the employee and the approver who
  // raised it. Each approver sees only their own threads; admins/HR see all.
  const allClarifications: any[] = Array.isArray(request?.clarifications)
    ? request.clarifications
    : [];
  const visibleClarifications = allClarifications.filter((c: any) => {
    if (isAdmin) return true;
    const raisedBy = String(c?.raised_by ?? "").trim().padStart(8, "0");
    return raisedBy === loggedInEmpId;
  });

  const approvalHistory: Partial<ApprovalStep>[] = (request?.history || []).map(
    (h: any) => {
      const statusMap: Record<string, ApprovalStep["status"]> = {
        APPROVE: "Approved",
        SUBMIT: "Approved",
        RESUBMIT: "Approved",
        AUTO_FORWARD: "Approved",
        REJECT: "Rejected",
        SEND_BACK: "Pending",
        CLARIFY: "Clarification",
        DISPATCH: "Dispatched",
        DEPT_APPROVE: "Approved",
        DEPT_CLEAR: "Approved",
        DEPT_REJECT: "DuesFound",
        SETTLE_DISPATCH: "Settled",
        SETTLE_ITEM: "Collected",
        SETTLE_CLOSE: "Collected",
      };
      return {
        level: h.role || `Level ${h.level_no ?? ""}`.trim(),
        status: statusMap[String(h.action ?? "").toUpperCase()] || "Pending",
        approver_name:
          h.approver_name || h.approver_emp_id || h.approver_empId || "—",
        approver_emp_no: h.approver_emp_id || h.approver_empId || "",
        action_date: h.acted_at || "",
        remarks: h.comments || "",
      };
    }
  );

  const REMARKS_MAX = 500;

  const handleAction = async (action: ApprovalActionType) => {
    if (!remarks.trim()) {
      toast.warning("Please provide remarks before proceeding.");
      return;
    }
    if (remarks.trim().length > REMARKS_MAX) {
      toast.warning(`Remarks cannot exceed ${REMARKS_MAX} characters.`);
      return;
    }
    const labels: Record<string, string> = {
      Approve: "approve",
      Reject: "reject",
      SendBack: "send back",
      Clarification: "seek clarification on",
    };
    const ok = await confirm(
      `Are you sure you want to ${labels[action]} this No-Dues request?`,
      {
        title: `Confirm ${action}`,
        confirmLabel: action,
        tone:
          action === "Approve"
            ? "success"
            : action === "Reject"
            ? "danger"
            : "primary",
      }
    );
    if (!ok) return;

    setActionLoading(true);
    const res =
      action === "Clarification"
        ? await raiseClarification({
            request_id: requestId,
            query_string: remarks.trim(),
          })
        : await submitApprovalAction({
            request_id: requestId,
            action,
            remarks: remarks.trim(),
          } as any);
    setActionLoading(false);

    if (res.success) {
      toast.success(
        `No-Dues request has been ${
          action === "Approve"
            ? "approved"
            : action === "Reject"
            ? "rejected"
            : action === "SendBack"
            ? "sent back"
            : "sent for clarification"
        }.`
      );
      navigate(-1);
    } else {
      toast.error(res.message || "Failed to process action. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <PageHeader title="No-Dues Review" onExit={() => navigate(-1)} />
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading No-Dues request...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content">
        <PageHeader
          title="No-Dues Review"
          right={<ApproverRoleBadge role={approverRole} />}
          onExit={() => navigate(-1)}
        />

        {/* Sub-header */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "#E6F1FB",
              color: "#185FA5",
            }}
          >
            <i className="ti ti-file-text" style={{ fontSize: 20 }} />
          </span>
          <div>
            <div className="fw-semibold">
              {request?.emp_name || "No-Dues Request"}
            </div>
            <div className="text-muted small">
              {requestId}
              {request?.emp_no ? ` • ${request.emp_no}` : ""}
            </div>
          </div>
        </div>

        {/* Employee Details */}
        <CollapsibleSection
          title="Employee Details"
          icon="ti-user"
          iconColor="#185FA5"
          iconBg="#E6F1FB"
          defaultExpanded
          badge={<StatusBadge status={request?.status || "In Review"} />}
        >
          <InfoRow label="Employee Name" value={request?.emp_name || "—"} />
          <InfoRow label="Employee No." value={request?.emp_no || "—"} />
          <InfoRow
            label="Mode of Separation"
            value={formatSeparationMode(request?.separation_type)}
          />
          <InfoRow label="Designation" value={request?.designation || "—"} />
          <InfoRow label="Department" value={request?.employeeDetails?.department_name || request?.posting || "—"} />
          <InfoRow label="Place of Posting" value={request?.employeeDetails?.place_of_posting || "—"} />
          <InfoRow label="Wing" value={request?.employeeDetails?.wing_name || request?.wing || "—"} />
          <InfoRow label="Pay Scale" value={request?.pay_scale || "—"} />
          <InfoRow label="Date of Joining" value={formatDate(request?.doj)} />
        </CollapsibleSection>

        {/* Employee-submitted No-Dues form */}
        <CollapsibleSection
          title="No-Dues Form Details"
          icon="ti-clipboard-list"
          iconColor="#BA7517"
          iconBg="#FFF3EC"
          defaultExpanded
        >
          {form ? (
            <>
              {FORM_FIELDS.map((f) => (
                <InfoRow
                  key={f.key}
                  label={f.label}
                  value={formValue(form, f.key, f.type)}
                />
              ))}
              <InfoRow
                label="Whether any training provided by DMRC (including Induction Course or nay other course) (in India/Abroad)"
                value={form.training_provided === "Y" ? "Yes" : "No"}
              />
              {form.training_provided === "Y" && (
                <InfoRow label="Training Details" value={formValue(form, "training_details")} />
              )}
              <InfoRow
                label="Whether any other Bond submitted to DMRC other than the Joining Bond"
                value={form.other_bond_submitted === "Y" ? "Yes" : "No"}
              />
              {form.other_bond_submitted === "Y" && (
                <InfoRow label="Other Bond Details" value={formValue(form, "other_bond_details")} />
              )}
              <InfoRow
                label="Sureties for Loans (Auto-fetched)"
                value={
                  form.loan_surety_flag === "Y"
                    ? formValue(form, "loan_surety_details") || "Yes"
                    : "No sureties on record"
                }
              />
              <InfoRow
                label="Declaration Accepted"
                value={form.declaration_accepted ? "Yes" : "No"}
              />
            </>
          ) : (
            <WarningBanner
              type="info"
              message="No-Dues form details are not available."
            />
          )}
        </CollapsibleSection>

        {/* Advances availed from DMRC — the fixed 4-type surety table the
            employee filled in (HBA / Vehicle / Multipurpose / DMRC Credit
            Society), each with up to 2 surety slots. */}
        {form && Array.isArray(form.advance_sureties) && form.advance_sureties.length > 0 && (
          <CollapsibleSection
            title="Advances Availed — Sureties Given"
            icon="ti-users-group"
            iconColor="#BA7517"
            iconBg="#FFF3EC"
            defaultExpanded
          >
            <div className="table-responsive">
              <table className="table table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 48 }}>S.No.</th>
                    <th>Type of Advance</th>
                    <th>Name, E.No. of employee for whom Surety is given</th>
                    <th>Name, E.No. of employee for whom Surety is given</th>
                  </tr>
                </thead>
                <tbody>
                  {ADVANCE_TYPE_LABELS.map((t, i) => {
                    const row = form.advance_sureties[i];
                    const firstLabel = row?.first_surety_emp_id
                      ? row.first_surety_emp_id === "N/A"
                        ? "N/A"
                        : `${row.first_surety_emp_name || ""} (${row.first_surety_emp_id})`.trim()
                      : "N/A";
                    const secondLabel = row?.second_surety_emp_id
                      ? row.second_surety_emp_id === "N/A"
                        ? "N/A"
                        : `${row.second_surety_emp_name || ""} (${row.second_surety_emp_id})`.trim()
                      : "N/A";
                    return (
                      <tr key={t.code}>
                        <td>{i + 1}</td>
                        <td className="fw-semibold">{t.label}</td>
                        <td>{firstLabel}</td>
                        <td>{secondLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Controlling & Reviewing Officers — captured on the employee's
            No-Dues form submission. */}
        {form &&
          (form.controlling_officer?.name ||
            form.controlling_officer?.emp_no ||
            (Array.isArray(form.reviewing_authorities) && form.reviewing_authorities.length > 0)) && (
            <CollapsibleSection
              title="Controlling & Reviewing Officers"
              icon="ti-users"
              iconColor="#185FA5"
              iconBg="#E6F1FB"
              defaultExpanded
            >
              {(form.controlling_officer?.name || form.controlling_officer?.emp_no) && (
                <>
                  <InfoRow label="Controlling Officer" value={form.controlling_officer.name || "—"} />
                  <InfoRow label="CO Employee No." value={form.controlling_officer.emp_no || "—"} />
                  <InfoRow label="CO Designation" value={form.controlling_officer.designation || "—"} />
                </>
              )}
              {(form.reviewing_authorities || []).map((a: any, i: number) => (
                <React.Fragment key={i}>
                  <InfoRow label={`${a.role} Name`} value={a.name || "—"} />
                  <InfoRow label={`${a.role} Employee No.`} value={a.emp_no || "—"} />
                </React.Fragment>
              ))}
            </CollapsibleSection>
          )}

        {/* Outstanding Loans — auto-captured from SAP */}
        <CollapsibleSection
          title="Outstanding Loans"
          icon="ti-cash"
          iconColor="#C0392B"
          iconBg="#FBE4E0"
          defaultExpanded
        >
          <OutstandingLoansSection
            loans={
              request?.outstanding_loans ||
              (Array.isArray(form?.outstanding_loans)
                ? form?.outstanding_loans
                : []) ||
              []
            }
            note="Auto-fetched from the employee's SAP loan records."
          />
          {form && (
            <InfoRow
              label="Advances availed from DMRC (HBA/Vehicle/Multipurpose/Laptop/DMRC Credit Society)"
              value={formValue(form, "advances_availed")}
            />
          )}
        </CollapsibleSection>

        {/* Clarifications (multi-round thread) — an approver only sees the
            threads they raised (chat stays private between that approver and the
            employee); admins/HR can see all. */}
        {visibleClarifications.length > 0 && (
          <CollapsibleSection
            title="Clarifications"
            icon="ti-messages"
            iconColor="#185FA5"
            iconBg="#E6F1FB"
            defaultExpanded
          >
            <ClarificationThread clarifications={visibleClarifications} />
          </CollapsibleSection>
        )}

        {/* Approval History */}
        <CollapsibleSection
          title="Approval History"
          icon="ti-git-merge"
          iconColor="#0F6E56"
          iconBg="#E1F5EE"
          defaultExpanded
        >
          {approvalHistory.length > 0 ? (
            <ApprovalTimeline steps={approvalHistory} />
          ) : (
            <p className="text-muted fst-italic mb-0">
              No approval actions taken yet.
            </p>
          )}
        </CollapsibleSection>

        {/* Action Panel — only the current approver may act */}
        {canAct ? (
          <SectionCard index={4} title="Your Action">
            <WarningBanner
              type="info"
              message={`You are reviewing as ${approverRole}. Your action will be recorded in the audit trail.`}
            />
            <label
              className="form-label text-uppercase fw-medium text-muted"
              style={{ fontSize: 11 }}
            >
              Remarks / Comments <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control mb-1"
              rows={4}
              maxLength={REMARKS_MAX}
              placeholder="Enter your remarks (mandatory for all actions)..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, REMARKS_MAX))}
            />
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="d-flex align-items-center gap-1 text-info small">
                <i className="ti ti-info-circle" />
                Note: You can enter a maximum of {REMARKS_MAX} characters.
              </span>
              <small className={remarks.length >= REMARKS_MAX ? "text-danger" : "text-muted"}>
                {remarks.length}/{REMARKS_MAX}
              </small>
            </div>
            {/* Send Back is disabled for now (matches the mobile app). */}
            <ActionButtonGroup
              onApprove={() => handleAction("Approve")}
              onReject={() => handleAction("Reject")}
              onClarification={() => handleAction("Clarification")}
              loading={actionLoading}
            />
          </SectionCard>
        ) : (
          <SectionCard index={4} title="Status">
            <WarningBanner
              type="info"
              message={
                isTerminalStatus
                  ? "This No-Dues request has reached a final status. No further action is required."
                  : "This request is awaiting another approver. You can view the details but cannot act on it right now."
              }
            />
          </SectionCard>
        )}
      </div>
    </div>
  );
};

export default NoDuesApproverReview;
